import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { showToastTC } from "../../../Utils/BaseApp";
import { fileKind } from "./utils";

interface ComposerProps {
  disabled?: boolean;
  onSend: (text: string, file: File | null, isVoice: boolean) => void;
}

type RecordingState = "idle" | "recording" | "paused" | "review";

const MAX_ROWS = 6;
const MAX_FILE_MB = 16;
const ACCEPT = "image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv";

// Se mantiene audio/webm (lo que el backend ya procesa para notas de voz)
function pickAudioMime(): string {
  if (typeof MediaRecorder === "undefined") return "";
  return MediaRecorder.isTypeSupported?.("audio/webm") ? "audio/webm" : "";
}

export const Composer: React.FC<ComposerProps> = ({ disabled, onSend }) => {
  const [text, setText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [recording, setRecording] = useState<RecordingState>("idle");
  const [seconds, setSeconds] = useState(0);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const discardRef = useRef(false);

  // Textarea autoajustable hasta MAX_ROWS líneas
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const lineHeight = parseFloat(window.getComputedStyle(el).lineHeight) || 20;
    el.style.height = `${Math.min(el.scrollHeight, lineHeight * MAX_ROWS)}px`;
  }, [text]);

  // Liberar la URL de previsualización cuando cambie o se desmonte
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // Al desmontar: detener micrófono y temporizador
  useEffect(() => {
    return () => {
      stopTimer();
      discardRef.current = true;
      if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  const startTimer = () => {
    stopTimer();
    timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const setAttachment = (next: File | null) => {
    setFile(next);
    setPreviewUrl(next ? URL.createObjectURL(next) : null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    e.target.value = ""; // permite volver a elegir el mismo archivo
    if (!selected) return;
    if (selected.size > MAX_FILE_MB * 1024 * 1024) {
      showToastTC(`El archivo supera ${MAX_FILE_MB} MB`, 4000, "error");
      return;
    }
    setAttachment(selected);
    textareaRef.current?.focus();
  };

  const clearAll = () => {
    setText("");
    setAttachment(null);
    setRecording("idle");
    setSeconds(0);
  };

  const send = () => {
    if (disabled) return;
    const trimmed = text.trim();
    if (!trimmed && !file) return;
    onSend(trimmed, file, recording === "review" || (file ? fileKind(file) === "audio" : false));
    clearAll();
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter envía; Shift+Enter (o Ctrl+Enter) inserta salto de línea
    if (e.key === "Enter" && !e.shiftKey && !e.ctrlKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    } else if (e.key === "Enter" && e.ctrlKey) {
      e.preventDefault();
      const el = e.currentTarget;
      const pos = el.selectionStart ?? text.length;
      setText(text.slice(0, pos) + "\n" + text.slice(el.selectionEnd ?? pos));
    }
  };

  const startRecording = async () => {
    if (recording === "paused") {
      recorderRef.current?.resume();
      setRecording("recording");
      startTimer();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickAudioMime();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      recorderRef.current = recorder;
      chunksRef.current = [];
      discardRef.current = false;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        if (discardRef.current) {
          chunksRef.current = [];
          return;
        }
        const type = "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        chunksRef.current = [];
        setAttachment(new File([blob], "audio_message.webm", { type }));
        setRecording("review");
      };

      recorder.start();
      setSeconds(0);
      setRecording("recording");
      startTimer();
    } catch (err) {
      showToastTC("No se pudo acceder al micrófono. Revisa los permisos del navegador.", 4000, "error");
    }
  };

  const pauseRecording = () => {
    recorderRef.current?.pause();
    setRecording("paused");
    stopTimer();
  };

  const stopRecording = () => {
    stopTimer();
    if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
  };

  const cancelRecording = () => {
    stopTimer();
    discardRef.current = true;
    if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
    clearAll();
  };

  const timeLabel = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
  const isRecordingUi = recording !== "idle";
  const canSend = !!text.trim() || !!file;

  const renderPreview = () => {
    if (!file || !previewUrl || recording === "review") return null;
    const kind = fileKind(file);
    return (
      <div className="wa-attachment">
        {kind === "image" && <img src={previewUrl} alt="Vista previa" />}
        {kind === "video" && <video src={previewUrl} muted />}
        {kind === "audio" && <audio src={previewUrl} controls />}
        {kind === "document" && (
          <div className="d-flex align-items-center gap-2 px-2">
            <i className="bi bi-file-earmark-text fs-4" />
            <span className="text-truncate" style={{ maxWidth: 220 }}>{file.name}</span>
          </div>
        )}
        <button type="button" className="wa-attachment-remove" onClick={() => setAttachment(null)} aria-label="Quitar adjunto">
          <i className="bi bi-x-lg" />
        </button>
      </div>
    );
  };

  return (
    <div className="wa-composer">
      {renderPreview()}
      <div className="wa-composer-row">
        {!isRecordingUi && (
          <>
            <button type="button" className="wa-icon-btn" onClick={() => fileInputRef.current?.click()} disabled={disabled} aria-label="Adjuntar archivo" title="Adjuntar">
              <i className="bi bi-paperclip" />
            </button>
            <input ref={fileInputRef} type="file" accept={ACCEPT} className="d-none" onChange={handleFileChange} />
            <textarea
              ref={textareaRef}
              className="wa-input"
              rows={1}
              value={text}
              disabled={disabled}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={file ? "Añade un comentario…" : "Escribe un mensaje"}
              aria-label="Mensaje"
            />
          </>
        )}

        {isRecordingUi && (
          <div className="wa-recorder">
            <button type="button" className="wa-icon-btn text-danger" onClick={cancelRecording} aria-label="Descartar nota de voz" title="Descartar">
              <i className="bi bi-trash" />
            </button>
            {recording === "review" && previewUrl ? (
              <audio src={previewUrl} controls className="flex-grow-1" />
            ) : (
              <div className="d-flex align-items-center flex-grow-1 gap-2">
                <span className={`wa-rec-dot ${recording === "recording" ? "wa-rec-dot--on" : ""}`} />
                <span className="font-monospace">{timeLabel}</span>
                <span className="text-body-secondary small">{recording === "paused" ? "En pausa" : "Grabando…"}</span>
              </div>
            )}
            {recording === "recording" && (
              <button type="button" className="wa-icon-btn" onClick={pauseRecording} aria-label="Pausar" title="Pausar">
                <i className="bi bi-pause-circle" />
              </button>
            )}
            {recording === "paused" && (
              <button type="button" className="wa-icon-btn" onClick={startRecording} aria-label="Continuar" title="Continuar">
                <i className="bi bi-mic" />
              </button>
            )}
            {(recording === "recording" || recording === "paused") && (
              <button type="button" className="wa-icon-btn text-danger" onClick={stopRecording} aria-label="Terminar grabación" title="Terminar">
                <i className="bi bi-stop-circle" />
              </button>
            )}
          </div>
        )}

        {canSend || recording === "review" ? (
          <button type="button" className="wa-send-btn" onClick={send} disabled={disabled || !canSend} aria-label="Enviar" title="Enviar (Enter)">
            <i className="bi bi-send-fill" />
          </button>
        ) : (
          !isRecordingUi && (
            <button type="button" className="wa-send-btn" onClick={startRecording} disabled={disabled} aria-label="Grabar nota de voz" title="Nota de voz">
              <i className="bi bi-mic-fill" />
            </button>
          )
        )}
      </div>
      {!isRecordingUi && <div className="wa-hint d-none d-md-block">Enter para enviar · Shift+Enter para nueva línea</div>}
    </div>
  );
};
